/* eslint-disable camelcase */
import { IPoi, TActivityType } from "~/interfaces";
import { IRouteConst } from "~/route/const";
import SceneCoords from "~/scene/common/coords";
import { IWasmStoreGeometry } from "~/wasm/store/interfaces";
import { TWasmTextureNames } from "~/wasm/textures-data";
import { getIcon } from "./providers/poiagg";
import { IPOIDescription } from "./providers/share-position/interfaces";

export function sceneCoordsToPoints(coords: Array<SceneCoords>) {
	const points: IWasmStoreGeometry["points"] = [];

	for (const coord of coords) {
		points.push(coord.x, coord.y);
	}

	return points;
}

const ACTIVITY_TEXTURE: {[key in TActivityType]: TWasmTextureNames } = {
	auto_moto: "activity-car",
	beh: "activity-run",
	bezky: "activity-cross-country-skis",
	gravel: "activity-gravel-bike",
	horolezectvi: "activity-alpinist",
	horske_kolo: "activity-mountain-bike",
	inline: "activity-inline-skates",
	jezdectvi: "activity-horse-riding",
	karavan: "activity-caravan",
	kocarek: "activity-stroller",
	kolobezka: "activity-kick-scooter",
	letadlo: "activity-plane",
	motorka: "activity-motorcycle",
	nakole: "activity-cycloturist",
	paddleboard: "activity-paddleboard",
	paraglide: "activity-paraglide",
	pesky: "activity-turist",
	silnicni_kolo: "activity-road-bike",
	sjezd: "activity-downhill-skis",
	skialpy: "activity-alpine-touring",
	sneznice: "activity-snowshoes",
	vodactvi: "activity-canoe",
	ekolo: "activity-ebike",
	ferrata: "activity-ferrata",
	brusle: "activity-ice-skates",
};

export function activityTypeToTexture(activityType: TActivityType) {
	return ACTIVITY_TEXTURE[activityType] || "";
}

const PATH_TEXTURE: {[key in TActivityType]: TWasmTextureNames } = {
	auto_moto: "path-car",
	beh: "path-run",
	bezky: "path-cross-country-skis",
	gravel: "path-gravel-bike",
	horolezectvi: "path-alpinist",
	horske_kolo: "path-mountain-bike",
	inline: "path-inline-skates",
	jezdectvi: "path-horse-riding",
	karavan: "path-caravan",
	kocarek: "path-stroller",
	kolobezka: "path-kick-scooter",
	letadlo: "path-plane",
	motorka: "path-motorcycle",
	nakole: "path-cycloturist",
	paddleboard: "path-paddleboard",
	paraglide: "path-paraglide",
	pesky: "path-turist",
	silnicni_kolo: "path-road-bike",
	sjezd: "path-downhill-skis",
	skialpy: "path-alpine-touring",
	sneznice: "path-snowshoes",
	vodactvi: "path-canoe",
	ekolo: "path-ebike",
	ferrata: "path-ferrata",
	brusle: "path-ice-skates",
};

export function pathTypeToTexture(pathType: TActivityType) {
	return PATH_TEXTURE[pathType] || "wasm-path";
}

const ROUTE_TEXTURE: {[key in keyof IRouteConst["types"]]: TWasmTextureNames } = {
	car: "route-car",
	ski: "route-cross-country-skis",
	bike: "route-cycloturist",
	pubt: "route-mhd",
	water: "route-canoe",
	trial: "route-turist",
};

const criterionMappingTable: {[key: string]: Array<number>} = {
	// auto
	car: [111, 112, 113, 114],
	// pesky
	trial: [134, 131, 132, 133],
	// kolo
	bike: [124, 123, 125, 121, 122],
	// lyze
	ski: [141, 142],
	// voda
	water: [144],
};

export function routeTypeToTexture(routeType: keyof IRouteConst["types"]) {
	return ROUTE_TEXTURE[routeType] || "";
}

export function routeCriterionToTexture(criterion: number) {
	const allKeys = Object.keys(criterionMappingTable);

	for (const key of allKeys) {
		if (criterionMappingTable[key].includes(criterion)) {
			// @ts-ignore
			return routeTypeToTexture(key);
		}
	}

	return "";
}

export function getMarkerImgFrompoi(poiData: IPoi & IPOIDescription) {
	let url = "";
	let forceImg = false;
	let media = poiData.media;

	// pro detail
	if (poiData.fromMarker && typeof poiData.fromMarker.getConf === "function" && poiData.fromMarker.getType().name === "big") {
		media = poiData.fromMarker.getPOI()?.media;
		forceImg = true;
	}

	// hledani
	if (media?.urls && media.urls["1x1"] && (poiData.markerId === "big" || forceImg)) {
		// puvodne bylo 60 - chceme x2; 120 obcas haze chybu
		url = media.urls["1x1"].replace("{width}", 100).replace("{height}", 100);
	} else if (poiData.icon && "1" in poiData.icon) {
		// zkusime u ikony nejdrive vetsi rozliseni
		url = poiData.icon["3"] || poiData.icon["2"] || poiData.icon["1"];
	} else {
		// jedno je pro hledani
		const typeId = poiData.poiTypeId || poiData.typeId;

		// zkusime poitype icon
		url = getIcon(typeId, {
			scale: 3,
		});
	}

	return url || "";
}

function imageLoad(image: HTMLImageElement): Promise<boolean> {
	return new Promise(resolve => {
		image.addEventListener("load", () => resolve(true));
		image.addEventListener("error", () => resolve(false));
	});
}

export async function getWasmScreenshot(appendToBody = true): Promise<null | HTMLImageElement> {
	const wasm = Mapy.getComponent("wasm").wasm;

	if (wasm) {
		const screenshotData = await wasm.getScreenshot();

		if (screenshotData) {
			const img = new Image();
			const loadProm = imageLoad(img);

			img.classList.add("wasmPrintImg");
			img.src = screenshotData.data;
			img.style.position = "absolute";
			img.style.left = "0";
			img.style.top = "0";
			img.style.width = `${screenshotData.width}px`;
			img.style.height = `${screenshotData.height}px`;
			img.style.zIndex = `1000`;
			appendToBody && document.getElementById("wasm").append(img);

			const loadData = await loadProm;

			if (loadData) {
				return img;
			}
		}
	}

	return null;
}

export function generatePopupAngles(arrowAngle: number) {
	const popupArrowAngles = [];

	for (let ind = 0; ind < 4; ind++) {
		const newAngle = (arrowAngle + (ind * 90)) % 360;

		popupArrowAngles.push(newAngle);
	}

	return popupArrowAngles;
}

export function isPoiSourceSafe(source: string) {
	return !["muni", "dist", "regi", "plot", "cada"].includes(source);
}
